import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './features/auth/auth-context.tsx';
import { RequireAuth } from './features/auth/require-auth.tsx';
import { AppLoading } from './features/app-loading.tsx';
// The two entry points people actually arrive on stay in the main bundle,
// so landing on either costs no extra round trip: the splash (marketing
// root) and the gym page every printed QR code points at.
import { SplashPage } from './features/splash/splash-page.tsx';
import { GymLanding } from './features/gym/gym-landing.tsx';

// Everything else is code-split.
//
// This used to statically import the whole guest flow, which meant a
// climber scanning a QR code on gym wifi downloaded the entire app before
// anything painted — the party page with its cinematic board, expedition
// and team views, plus jsqr (~250 KB of source) for camera QR scanning and
// the qrcode generator. None of that is needed to render a gym's landing
// page. Splitting it is worth far more than a round trip to whoever is
// standing at the wall on two bars of signal.
//
// Each lazy() returns the named export wrapped as default, since
// React.lazy only accepts modules with a default export.
function lazyNamed<T extends Record<string, ComponentType<unknown>>>(
  loader: () => Promise<T>,
  exportName: keyof T,
) {
  return lazy(() => loader().then((m) => ({ default: m[exportName] })));
}

// Guest flow — split out of the entry chunk. These load on navigation,
// by which point the landing page is already on screen.
const LoginPage = lazyNamed(() => import('./features/auth/login-page.tsx'), 'LoginPage');
const SignupPage = lazyNamed(() => import('./features/auth/signup-page.tsx'), 'SignupPage');
const RouteDetailPage = lazyNamed(() => import('./features/gym/route-detail-page.tsx'), 'RouteDetailPage');
const PartyCreatePage = lazyNamed(() => import('./features/party/party-create-page.tsx'), 'PartyCreatePage');
// Pulls in jsqr for camera scanning — by far the heaviest guest dependency.
const PartyJoinPage = lazyNamed(() => import('./features/party/party-join-page.tsx'), 'PartyJoinPage');
// The biggest page in the app: board, expedition and team views.
const PartyBashPage = lazyNamed(() => import('./features/party/party-bash-page.tsx'), 'PartyBashPage');
const GymDailyLeaderboardPage = lazyNamed(() => import('./features/leaderboard/gym-daily-page.tsx'), 'GymDailyLeaderboardPage');
// Wall displays — full-screen, read-only, meant for a TV in the gym.
const GymLeaderboardLivePage = lazyNamed(() => import('./features/leaderboard/gym-daily-page.tsx'), 'GymLeaderboardLivePage');
const HangboardLivePage = lazyNamed(() => import('./features/hangboard/hangboard-live-page.tsx'), 'HangboardLivePage');
const PostersPage = lazyNamed(() => import('./features/posters/posters-page.tsx'), 'PostersPage');

const AdminHome = lazyNamed(() => import('./features/admin/admin-home.tsx'), 'AdminHome');
const GymCreatePage = lazyNamed(() => import('./features/admin/gym-create-page.tsx'), 'GymCreatePage');
const GymAdminLayout = lazyNamed(() => import('./features/admin/gym-layout.tsx'), 'GymAdminLayout');
const GymConfigPage = lazyNamed(() => import('./features/admin/gym-config-page.tsx'), 'GymConfigPage');
const RoutesPage = lazyNamed(() => import('./features/admin/routes-page.tsx'), 'RoutesPage');
const LayoutPage = lazyNamed(() => import('./features/admin/layout-page.tsx'), 'LayoutPage');
const ModifiersPage = lazyNamed(() => import('./features/admin/modifiers-page.tsx'), 'ModifiersPage');
const SettersPage = lazyNamed(() => import('./features/admin/setters-page.tsx'), 'SettersPage');
const QrPage = lazyNamed(() => import('./features/admin/qr-page.tsx'), 'QrPage');
const HangboardAdminPage = lazyNamed(() => import('./features/admin/hangboard-admin-page.tsx'), 'HangboardAdminPage');
const DisplaysPage = lazyNamed(() => import('./features/admin/displays-page.tsx'), 'DisplaysPage');
const HangboardPage = lazyNamed(() => import('./features/hangboard/hangboard-page.tsx'), 'HangboardPage');
const GymReportingPage = lazyNamed(() => import('./features/admin/reporting-page.tsx'), 'GymReportingPage');
const SetterDashboard = lazyNamed(() => import('./features/setter/setter-dashboard.tsx'), 'SetterDashboard');
const SuperAdminPage = lazyNamed(() => import('./features/super-admin/super-admin-page.tsx'), 'SuperAdminPage');
const OwnersPage = lazyNamed(() => import('./features/super-admin/owners-page.tsx'), 'OwnersPage');
const UsersPage = lazyNamed(() => import('./features/super-admin/users-page.tsx'), 'UsersPage');
const SuperAdminReportingPage = lazyNamed(() => import('./features/super-admin/reporting-page.tsx'), 'SuperAdminReportingPage');
const AiUsagePage = lazyNamed(() => import('./features/super-admin/ai-usage-page.tsx'), 'AiUsagePage');
const PartySummaryPage = lazyNamed(() => import('./features/party/party-summary-page.tsx'), 'PartySummaryPage');
const ProfilePage = lazyNamed(() => import('./features/profile/profile-page.tsx'), 'ProfilePage');
const PublicProfilePage = lazyNamed(() => import('./features/profile/public-profile-page.tsx'), 'PublicProfilePage');
const PricingPage = lazyNamed(() => import('./features/pricing/pricing-page.tsx'), 'PricingPage');
const OnboardGymPage = lazyNamed(() => import('./features/pricing/onboard-gym-page.tsx'), 'OnboardGymPage');
const SupportPage = lazyNamed(() => import('./features/support/support-page.tsx'), 'SupportPage');
const SubmissionsPage = lazyNamed(() => import('./features/super-admin/submissions-page.tsx'), 'SubmissionsPage');
const NotFoundPage = lazyNamed(() => import('./features/not-found-page.tsx'), 'NotFoundPage');

// Same visual as the inline boot splash, so a lazy chunk arriving looks
// like the page is still loading rather than a second loading screen.
function RouteLoading() {
  return <AppLoading />;
}

export function AppRoutes() {
  return (
    <AuthProvider>
      <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path="/" element={<SplashPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route
          path="/profile"
          element={
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          }
        />
        <Route path="/profile/:userId" element={<PublicProfilePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route
          path="/onboard-gym"
          element={
            <RequireAuth>
              <OnboardGymPage />
            </RequireAuth>
          }
        />

        <Route path="/g/:gymSlug" element={<GymLanding />} />
        <Route path="/g/:gymSlug/r/:routeId" element={<RouteDetailPage />} />
        <Route path="/g/:gymSlug/party/new" element={<PartyCreatePage />} />
        <Route path="/g/:gymSlug/party/join" element={<PartyJoinPage />} />
        <Route path="/g/:gymSlug/party/:partyId/bash" element={<PartyBashPage />} />
        <Route path="/g/:gymSlug/party/:partyId/leaderboard" element={<PartySummaryPage />} />
        <Route path="/g/:gymSlug/leaderboard" element={<GymDailyLeaderboardPage />} />
        <Route path="/g/:gymSlug/leaderboard/live" element={<GymLeaderboardLivePage />} />
        {/* Where the printed hangboard QR code lands. Public on purpose. */}
        <Route path="/g/:gymSlug/hangboard" element={<HangboardPage />} />
        <Route path="/g/:gymSlug/hangboard/live" element={<HangboardLivePage />} />
        {/* Print-ready wall posters. Public so an admin can hand the link
            to whoever is at the printer. */}
        <Route path="/g/:gymSlug/posters" element={<PostersPage />} />

        <Route
          path="/admin"
          element={
            <RequireAuth>
              <AdminHome />
            </RequireAuth>
          }
        />
        <Route
          path="/super-admin/gym/new"
          element={
            <RequireAuth>
              <GymCreatePage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/gym/:gymId"
          element={
            <RequireAuth>
              <GymAdminLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="config" replace />} />
          <Route path="config" element={<GymConfigPage />} />
          <Route path="routes" element={<RoutesPage />} />
          <Route path="layout" element={<LayoutPage />} />
          <Route path="modifiers" element={<ModifiersPage />} />
          <Route path="setters" element={<SettersPage />} />
          <Route path="hangboard" element={<HangboardAdminPage />} />
          <Route path="displays" element={<DisplaysPage />} />
          <Route path="qr" element={<QrPage />} />
          <Route path="reporting" element={<GymReportingPage />} />
        </Route>

        <Route
          path="/setter/gym/:gymId"
          element={
            <RequireAuth>
              <SetterDashboard />
            </RequireAuth>
          }
        />

        <Route
          path="/super-admin"
          element={
            <RequireAuth>
              <SuperAdminPage />
            </RequireAuth>
          }
        />
        <Route
          path="/super-admin/owners"
          element={
            <RequireAuth>
              <OwnersPage />
            </RequireAuth>
          }
        />
        <Route
          path="/super-admin/users"
          element={
            <RequireAuth>
              <UsersPage />
            </RequireAuth>
          }
        />
        <Route
          path="/super-admin/reporting"
          element={
            <RequireAuth>
              <SuperAdminReportingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/super-admin/ai"
          element={
            <RequireAuth>
              <AiUsagePage />
            </RequireAuth>
          }
        />

        <Route path="/support" element={<SupportPage />} />
        <Route
          path="/super-admin/inbox"
          element={
            <RequireAuth>
              <SubmissionsPage />
            </RequireAuth>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </AuthProvider>
  );
}
